<template>
  <teleport to="body" v-if="accessAllowed">
    <AgentChatWindow />
    <div class="fixed bottom-4 right-4 z-50">
      <button
        type="button"
        class="relative flex items-center justify-center w-16 h-16 rounded-full shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-primary focus-visible:ring-offset-2"
        :class="agentStore.isOpen ? 'bg-blue-primary text-white' : 'bg-white ring-1 ring-black/5'"
        :aria-label="agentStore.isOpen ? 'Minimize SAMMI' : 'Open SAMMI, the TSAMM assistant'"
        :title="agentStore.isOpen ? 'Minimize SAMMI' : 'Chat with SAMMI'"
        @click="agentStore.isOpen = !agentStore.isOpen"
        @mouseenter="onHover(true)"
        @mouseleave="onHover(false)"
      >
        <!-- Open: minimize affordance -->
        <Icon v-if="agentStore.isOpen" name="heroicons:chevron-down" class="w-7 h-7" />

        <!-- Closed: animated SAMMI mascot. Faces are stacked and cross-faded so
             expression changes morph smoothly instead of flashing. -->
        <span v-else class="sammi-bob relative block w-11 h-11">
          <img
            v-for="face in FACE_KEYS"
            :key="face"
            :src="`/sammi/${face}.svg`"
            alt=""
            aria-hidden="true"
            class="absolute inset-0 w-full h-full object-contain transition-opacity duration-200"
            :style="{ opacity: currentFace === face ? 1 : 0 }"
          />
        </span>
      </button>
    </div>
  </teleport>
</template>
<script setup lang="ts">
const agentStore = useAgentStore();

const FACE_KEYS = ['look', 'look-left', 'look-right', 'look-up', 'look-down', 'smile', 'squint', 'thinking'] as const;
const REST = 'look';
const GLANCES = ['look-left', 'look-right', 'look-up', 'look-down', 'smile', 'squint'] as const;

const currentFace = ref<(typeof FACE_KEYS)[number]>(REST);
const hovering = ref(false);
// SAMMI "thinks" whenever a turn is in flight
const thinking = computed(() => agentStore.status === 'submitted' || agentStore.status === 'streaming');

let glanceTimer: ReturnType<typeof setTimeout> | undefined;
let returnTimer: ReturnType<typeof setTimeout> | undefined;

// Idle behavior: rest on the centered face, occasionally glance around, then
// settle back — gives the mascot a bit of life without being distracting.
const scheduleGlance = () => {
  glanceTimer = setTimeout(
    () => {
      if (!thinking.value && !hovering.value && !agentStore.isOpen) {
        currentFace.value = GLANCES[Math.floor(Math.random() * GLANCES.length)];
        returnTimer = setTimeout(() => {
          if (!thinking.value && !hovering.value) currentFace.value = REST;
        }, 750);
      }
      scheduleGlance();
    },
    2200 + Math.random() * 2200
  );
};

const onHover = (isOver: boolean) => {
  hovering.value = isOver;
  if (thinking.value) return;
  currentFace.value = isOver ? 'smile' : REST;
};

// While working, hold the "thinking" face; return to rest when done.
watch(thinking, (isThinking) => {
  currentFace.value = isThinking ? 'thinking' : hovering.value ? 'smile' : REST;
});

onMounted(scheduleGlance);
onBeforeUnmount(() => {
  clearTimeout(glanceTimer);
  clearTimeout(returnTimer);
});

// User-gated rollout: ask the server whether SAMMI is available for this user
// (feature flag + optional SAMMI_USERS allowlist). The chat route enforces the
// same checks regardless — this only controls whether the bubble renders.
const accessAllowed = ref(false);
onMounted(async () => {
  try {
    const token = exportUserSession().value;
    if (!token) return;
    const res = await $fetch<{ enabled: boolean }>('/api/agent/enabled', {
      headers: { Authorization: `Bearer ${token}` },
    });
    accessAllowed.value = !!res?.enabled;
  } catch {
    accessAllowed.value = false;
  }
});
</script>
<style scoped>
.sammi-bob {
  animation: sammi-bob 3.2s ease-in-out infinite;
}
@keyframes sammi-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .sammi-bob {
    animation: none;
  }
}
</style>
